import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import './index.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const RevenueChart = () => {
  const api = process.env.REACT_APP_API;
  const [revenueData, setRevenueData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Total Revenue',
        backgroundColor: 'rgba(75,192,192,0.6)',
        borderColor: 'rgba(75,192,192,1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(75,192,192,0.8)',
        hoverBorderColor: 'rgba(75,192,192,1)',
        data: []
      },
      {
        label: 'Collected Revenue',
        backgroundColor: 'rgba(153,102,255,0.6)',
        borderColor: 'rgba(153,102,255,1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(153,102,255,0.8)',
        hoverBorderColor: 'rgba(153,102,255,1)',
        data: []
      }
    ]
  });

  useEffect(() => {
    const fetchRevenueData = async () => {
      try {
        const token = localStorage.getItem("jwt_token");

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const admissionsResponse = await fetch(`${api}/admissions`, { headers });
        const feesResponse = await fetch(`${api}/fees`, { headers });

        if (!admissionsResponse.ok || !feesResponse.ok) {
          throw new Error('Failed to fetch data');
        }

        const admissionsDataRaw = await admissionsResponse.json();
        const feesDataRaw = await feesResponse.json();

        const admissionsData = Array.isArray(admissionsDataRaw)
          ? admissionsDataRaw
          : admissionsDataRaw.data || admissionsDataRaw.admissions || [];

        const feesData = Array.isArray(feesDataRaw)
          ? feesDataRaw
          : feesDataRaw.data || [];

        const revenueByCenter = admissionsData.reduce((acc, admission) => {
          if (!admission.centerName || typeof admission.totalFees !== 'number') return acc;
          acc[admission.centerName] = acc[admission.centerName] || {
            totalRevenue: 0,
            collectedRevenue: 0
          };
          acc[admission.centerName].totalRevenue += admission.totalFees;
          return acc;
        }, {});

        feesData.forEach(fee => {
          fee.terms?.forEach(term => {
            if (
              fee.center &&
              revenueByCenter[fee.center] &&
              typeof term.amountPaid === 'number'
            ) {
              revenueByCenter[fee.center].collectedRevenue += term.amountPaid;
            }
          });
        });

        const centers = Object.keys(revenueByCenter);
        const totalRevenue = centers.map(center => revenueByCenter[center].totalRevenue);
        const collectedRevenue = centers.map(center => revenueByCenter[center].collectedRevenue || 0);

        setRevenueData({
          labels: centers,
          datasets: [
            {
              ...revenueData.datasets[0],
              data: totalRevenue
            },
            {
              ...revenueData.datasets[1],
              data: collectedRevenue
            }
          ]
        });
      } catch (error) {
        console.error('Error fetching revenue data:', error);
      }
    };

    fetchRevenueData();
  }, []);

  return (
    <div className="revenue-chart-container" style={{ height: '500px', padding: '20px' }}>
      <h2 className="revenue-chart-title">Center Revenue</h2>
      <Bar
        data={revenueData}
        options={{
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: value => `${(value / 1000).toFixed(0)}K`
              }
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'top'
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  return `${context.dataset.label}: ₹${context.formattedValue}`;
                }
              }
            }
          }
        }}
      />
    </div>
  );
};

export default RevenueChart;
